import Modal from "@/components/controls/Modal";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";
import { InputProps } from "@/components/ui/input";
import { useCreateViewMutation } from "@/entities/views/hooks";
import { ViewData, ViewDataConfiguration } from "@/entities/views/types";
import toast from "react-hot-toast";
import { Close } from "@dreamhub/designsystem/icons";
import {
  deleteTempView,
  getColumnsToSave,
  getTempView
} from "@/entities/dataTables/helpers";
import { ColumnData } from "@/entities/dataTables/types";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  viewType: number;
  filters: any;
  sortBy: any;
  groupBy: string[];
  searchTerm: string;
  columnsState: ColumnData[];
  selectedView: ViewDataConfiguration;
  onSuccess?: (view: ViewDataConfiguration) => void;
}

const NewNameViewModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  viewType,
  filters,
  sortBy,
  groupBy,
  searchTerm,
  columnsState,
  selectedView,
  onSuccess
}) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const createView = useCreateViewMutation();
  const [isFormValid, setIsFormValid] = useState(false);

  const formSections: FormSectionProps[] = [
    {
      id: "nameSection",
      fields: [
        {
          name: "name",
          required: true,
          type: "text",
          fieldActionProps: {
            placeholder: t("datatoolbar.views.namePlaceholder"),
            maxLength: 100
          } as InputProps,
          showRequiredIndicator: false,
          defaultValue: t("datatoolbar.views.exampleView"),
          validation: {
            required: false,
            customRules: [
              {
                validate: (value: string) => {
                  if (value === undefined || value.trim() === "") {
                    return false;
                  }
                  return true;
                },
                message: t("datatoolbar.views.enterViewName")
              }
            ]
          }
        } as FormFieldNewType
      ]
    }
  ];

  useEffect(() => {
    if (isOpen) {
      formRef.current?.reset();
    }
  }, [isOpen]);

  const handleSubmit = async (data: ViewData) => {
    const trimmedData = { ...data, name: data.name?.trim() };
    try {
      const tempView = getTempView(selectedView);
      const formattedConfig = {
        ...(tempView?.config || { filters, groupBy, searchTerm }),
        sort: tempView?.config?.sort ? tempView.config.sort : sortBy,
        columns: getColumnsToSave(selectedView, columnsState)
      };
      const newView = {
        ...trimmedData,
        type: viewType,
        config: formattedConfig,
        isSystem: false
      };

      await createView.mutateAsync(newView, {
        onSuccess: (response, newView) => {
          deleteTempView(selectedView);
          toast.success(
            t("datatoolbar.views.createSuccess", { viewName: newView?.name })
          );
          onSuccess?.({ ...newView, id: response.id } as ViewDataConfiguration);
        },
        onError: (error: Error) => {
          console.error("Error creating view:", error);
          showErrorToast();
        }
      });
    } catch (err) {
      console.error("Error creating view:", err);
    }
    setIsOpen(false);
  };

  const showErrorToast = () => {
    toast.error(
      <ToastBody
        title={t("datatoolbar.views.errorSaving")}
        actions={
          <IconButton
            size="md"
            variant="iconNoBg"
            iconColor="secondary"
            onClick={() => toast.dismiss()}
          >
            <Close />
          </IconButton>
        }
      />,
      { icon: null }
    );
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleCreate = () => {
    formRef.current?.requestSubmit();
  };

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      contentClassname="w-[35rem]"
      headerClassname="pl-5 border-none"
      hideDivider
      title={
        <Text as="div" variant="lgSubtitleH1" className="normal-case">
          {t("datatoolbar.views.nameView")}
        </Text>
      }
      content={
        <div className="flex flex-col w-full pb-4 px-6 items-stretch">
          <div className="px-1 py-2">
            <FormNew
              sections={formSections}
              isFormValid={(isValid: boolean) => setIsFormValid(isValid)}
              onSubmit={handleSubmit}
              formRef={formRef}
            />
          </div>
        </div>
      }
      footer={
        <div className="flex items-end w-full justify-end gap-2">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={createView.isPending}
          >
            {t("cancel")}
          </Button>
          <Button
            disabled={!isFormValid || createView.isPending}
            onClick={handleCreate}
          >
            {t("datatoolbar.views.save")}
          </Button>
        </div>
      }
    />
  );
};

export default NewNameViewModal;
